// lib/workspace-timeline.ts

import {
  resolveStudioLayerTransform,
  STUDIO_LAYER_FLIP_X_PARAM_KEY,
  STUDIO_LAYER_FLIP_Y_PARAM_KEY,
  STUDIO_LAYER_ROTATION_PARAM_KEY,
} from '#lib/studio-layer-transform';
import { STUDIO_MODEL_REGISTRY, type StudioModelId } from '#lib/model-registry';
import {
  resolvePendingTrackModelName,
  resolvePendingVideoGenerationTrack,
  type PendingVideoGenerationTaskSource,
  type PendingVideoGenerationTrack,
} from '#lib/timeline-generation-placeholders';
import { videoLoopStageLabel } from '#lib/video-loop-tiers';
import {
  clamp,
  normalizeTimelineTrackPlaybackRate,
  toFinite,
} from '#lib/timeline-video-types';

export const TIMELINE_AUDIO_ONLY_PARAM_KEY = '__timelineAudioOnly';
export const TIMELINE_MUTE_AUDIO_PARAM_KEY = '__timelineMuteAudio';
export const TIMELINE_AUDIO_GAIN_PARAM_KEY = '__timelineAudioGain';
export const TIMELINE_SEPARATED_FROM_LAYER_ID_PARAM_KEY = '__timelineSeparatedFromLayerId';
export const TIMELINE_FADE_IN_RATIO_PARAM_KEY = '__timelineFadeInRatio';
export const TIMELINE_FADE_OUT_RATIO_PARAM_KEY = '__timelineFadeOutRatio';
export const TIMELINE_PLAYBACK_RATE_PARAM_KEY = '__timelinePlaybackRate';
export const TIMELINE_LANE_ID_PARAM_KEY = '__timelineLaneId';
export const TIMELINE_LANE_ORDER_PARAM_KEY = '__timelineLaneOrder';

const TIMELINE_PARAM_SIGNATURE_KEYS = [
  TIMELINE_AUDIO_ONLY_PARAM_KEY,
  TIMELINE_MUTE_AUDIO_PARAM_KEY,
  TIMELINE_AUDIO_GAIN_PARAM_KEY,
  TIMELINE_SEPARATED_FROM_LAYER_ID_PARAM_KEY,
  TIMELINE_FADE_IN_RATIO_PARAM_KEY,
  TIMELINE_FADE_OUT_RATIO_PARAM_KEY,
  TIMELINE_PLAYBACK_RATE_PARAM_KEY,
  TIMELINE_LANE_ID_PARAM_KEY,
  TIMELINE_LANE_ORDER_PARAM_KEY,
  STUDIO_LAYER_ROTATION_PARAM_KEY,
  STUDIO_LAYER_FLIP_X_PARAM_KEY,
  STUDIO_LAYER_FLIP_Y_PARAM_KEY,
] as const;

export const AUDIO_GAIN_MAX = 8;

export type WorkspaceTimelineLayerKind = 'image' | 'video' | 'audio' | null;

interface WorkspaceTimelineRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface WorkspaceTimelineSummary {
  workspaceId: string;
  hasTimelineTracks: boolean;
  hasPlayableMediaLayers: boolean;
  hasInitializedImageTimeline: boolean;
  explicitMaxTrackEndMs: number;
  durationMs: number;
  cursorMs: number;
  fps: number | null;
  preferVersionView: boolean;
}

export interface WorkspaceTimelineTrackView {
  layerId: string;
  layerType: 'image' | 'video' | 'audio';
  name: string;
  visible: boolean;
  renderOrder: number;
  topLayer: boolean;
  startMs: number;
  endMs: number | null;
  sourceInMs: number;
  playbackRate: number;
  rect: WorkspaceTimelineRect;
  scale: number;
  rotationDeg: number;
  flipX: boolean;
  flipY: boolean;
  opacity: number;
  imageSrc: string | null;
  videoSrc: string | null;
  audioSrc: string | null;
  mutedInTimeline: boolean;
  audioGain: number;
  separatedFromLayerId: string | null;
  fadeInRatio: number;
  fadeOutRatio: number;
  laneId: string;
  laneOrder: number;
}

interface WorkspaceTimelineLaneView {
  laneId: string;
  laneOrder: number;
  trackLayerIds: string[];
  previewLayerId: string;
}

export interface WorkspaceTimelineSnapshot {
  workspaceId: string;
  projectId: string;
  summary: WorkspaceTimelineSummary;
  trackLayerIds: string[];
  tracks: WorkspaceTimelineTrackView[];
  lanes: WorkspaceTimelineLaneView[];
}

export interface WorkspaceTimelineResolvedTrackView extends Omit<WorkspaceTimelineTrackView, 'endMs'> {
  endMs: number;
  sourceOutMs: number;
  activeDurationMs: number;
  mediaDurationMs: number | null;
  trackLimitEndMs: number;
  minSegmentMs: number;
  fadeInEndMs: number;
  fadeOutStartMs: number;
}

export interface WorkspaceTimelineLaneRowView {
  laneId: string;
  laneOrder: number;
  tracks: WorkspaceTimelineResolvedTrackView[];
  previewLayerId: string;
}

export interface WorkspaceTimelineRangeView {
  playbackStartMs: number;
  playbackEndMs: number;
  rulerRangeStartMs: number;
  rulerRangeEndMs: number;
  rulerRangeMs: number;
  timelineRenderRangeStartMs: number;
  timelineRenderRangeEndMs: number;
  timelineRenderRangeMs: number;
}

export interface WorkspaceTimelineDurationMetrics {
  maxDeclaredTrackEndMs: number;
  maxInferredTrackEndMs: number;
  durationMs: number;
}

export interface WorkspaceTimelineDurationTrackLike {
  layerId: string;
  startMs: number;
  endMs: number | null;
  sourceInMs: number;
}

/**
 * Canonical DB/Kernel timeline projection. This is intentionally DOM-free and
 * agent-compatible: it only uses persisted layer/workspace fields and Kernel
 * runtime selection/tasks passed in args. UI render hooks may enrich timings
 * with probed HTMLMediaElement durations until those durations are persisted.
 */
export interface WorkspaceTimelineProjection {
  workspaceId: string;
  projectId: string;
  summary: WorkspaceTimelineSummary;
  trackLayerIds: string[];
  tracks: WorkspaceTimelineTrackView[];
  resolvedTracks: WorkspaceTimelineResolvedTrackView[];
  visibleResolvedTracks: WorkspaceTimelineResolvedTrackView[];
  lanes: WorkspaceTimelineLaneView[];
  laneRows: WorkspaceTimelineLaneRowView[];
  selectedTrackLayerIds: string[];
  selectedTracks: WorkspaceTimelineResolvedTrackView[];
  pendingGenerationTracks: WorkspacePendingGenerationTrackSignatureEntry[];
  range: WorkspaceTimelineRangeView;
}

export interface WorkspaceTimelineProjectionArgs {
  projectId: string;
  workspaceId: string;
  workspace: WorkspaceTimelineWorkspaceSource;
  layerRegistry: Record<string, WorkspaceTimelineLayerSource | undefined>;
  projectRuntimeSelection?: readonly unknown[] | null;
  pendingTasks?: readonly WorkspacePendingGenerationTaskSource[] | null;
  options?: {
    fallbackTrackDurationMs?: number;
    minTrackSpanMs?: number;
  };
}

export interface WorkspaceTimelinePointerTrackLike {
  layerId: string;
  startMs: number;
  endMs: number | null;
  renderOrder: number;
}

interface WorkspaceTimelineLaneElementLike {
  getBoundingClientRect: () => { left: number; width: number };
}

/**
 * UI pointer resolver input. This intentionally depends on a layout source
 * (`getBoundingClientRect`) and must stay separate from DOM-free Kernel getters.
 */
export interface WorkspaceTimelinePointerResolveArgs<TTrack extends WorkspaceTimelinePointerTrackLike = WorkspaceTimelinePointerTrackLike> {
  workspaceId?: string;
  laneTracks: readonly TTrack[];
  laneElement: WorkspaceTimelineLaneElementLike | null;
  clientX: number;
  timelineRenderRangeStartMs: number;
  timelineRenderRangeMs: number;
}

export interface WorkspaceTimelineSplitTrackLike {
  layerId?: string;
  startMs: number;
  endMs: number | null;
}

/** UI pointer resolver input; not suitable for agentic/headless commands. */
export interface WorkspaceTimelineSplitResolveArgs<TTrack extends WorkspaceTimelineSplitTrackLike = WorkspaceTimelineSplitTrackLike> {
  track: TTrack;
  laneElement: WorkspaceTimelineLaneElementLike | null;
  clientX: number;
  timelineRenderRangeStartMs: number;
  timelineRenderRangeMs: number;
  snapToSecondThresholdMs?: number;
  edgeSnapThresholdMs?: number;
}

export type WorkspaceTimelineLayerSource = {
  id?: string | null;
  renderOrder?: number | null;
  topLayer?: boolean | null;
  'top-layer'?: boolean | null;
  imageSrc?: string | null;
  rect?: { x?: unknown; y?: unknown; w?: unknown; h?: unknown } | null;
  scale?: number | null;
  visible?: boolean | null;
  name?: string | null;
  type?: string | null;
  params?: unknown;
  opacity?: number | null;
  videoSrc?: string | null;
  timelineStartMs?: string | number | null;
  timelineEndMs?: string | number | null;
  timelineSourceInMs?: string | number | null;
};

type WorkspaceTimelineWorkspaceSource = {
  id?: string | null;
  projectId?: string | null;
  layerIds?: unknown[] | null;
  timelineDurationMs?: string | number | null;
  timelineCursorMs?: string | number | null;
  timelineFps?: number | null;
  preferVersionPanelOverTimeline?: boolean | null;
  imageTimelineManuallyEnabled?: boolean | null;
} | null | undefined;

export type WorkspaceTimelineSummaryWorkspaceSource = Pick<NonNullable<WorkspaceTimelineWorkspaceSource>,
  'layerIds' | 'timelineDurationMs' | 'timelineCursorMs' | 'timelineFps' | 'preferVersionPanelOverTimeline' | 'imageTimelineManuallyEnabled'
>;

export type WorkspaceTimelineSummaryLayerSource = Pick<WorkspaceTimelineLayerSource,
  'type' | 'videoSrc' | 'timelineStartMs' | 'timelineEndMs'
>;

export type WorkspacePendingGenerationTaskSource = PendingVideoGenerationTaskSource;

export type WorkspacePendingGenerationTrackSignatureEntry = PendingVideoGenerationTrack;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// Timeline-domain aliases of the shared numeric primitives; the bodies live in
// #lib/timeline-video-types so preview and export never clamp differently.
export const toFiniteTimelineMs = toFinite;

export const clampTimelineNumber = clamp;

const normalizeTimelineParamValue = (value: unknown): string => {
  if (value === null || typeof value === 'undefined') return '';
  if (typeof value === 'boolean') return value ? '1' : '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : '';
  }
  return '';
};

const getTimelineParamsSignature = (params: unknown): string => {
  if (!isRecord(params)) return '';
  return TIMELINE_PARAM_SIGNATURE_KEYS
    .map((key) => {
      const normalized = normalizeTimelineParamValue(params[key]);
      return normalized ? `${key}:${normalized}` : '';
    })
    .filter(Boolean)
    .join('|');
};

export const buildWorkspaceTimelineTrackDataSignature = (
  layerIds: readonly string[],
  getLayer: (layerId: string) => WorkspaceTimelineLayerSource | null | undefined
): string => {
  if (layerIds.length === 0) return '';

  return layerIds.map((layerId) => {
    const layer = getLayer(layerId);
    if (!layer) return `${layerId}:missing`;
    const legacyTopLayer = Reflect.get(layer, 'top-layer') === true;
    return [
      layer.id ?? layerId,
      layer.type ?? '',
      layer.visible === false ? '0' : '1',
      layer.name ?? '',
      layer.videoSrc ?? '',
      layer.imageSrc ?? '',
      typeof layer.renderOrder === 'number' && Number.isFinite(layer.renderOrder) ? layer.renderOrder : '',
      legacyTopLayer || layer.topLayer === true ? 'top' : '',
      normalizeLayerOpacity(layer.opacity),
      toFiniteTimelineMs(layer.timelineStartMs) ?? '',
      toFiniteTimelineMs(layer.timelineEndMs) ?? '',
      toFiniteTimelineMs(layer.timelineSourceInMs) ?? '',
      timelineRectSignature(layer.rect),
      typeof layer.scale === 'number' && Number.isFinite(layer.scale) ? layer.scale : '',
      getTimelineParamsSignature(layer.params),
    ].join('~');
  }).join('|');
};

export const readTimelineParamNumber = (params: unknown, key: string): number | null => {
  if (!isRecord(params)) return null;
  return toFiniteTimelineMs(params[key]);
};

export const readTimelineParamString = (params: unknown, key: string): string | null => {
  if (!isRecord(params)) return null;
  const value = params[key];
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const readTimelineParamFlag = (params: unknown, key: string): boolean => {
  if (!isRecord(params)) return false;
  const value = params[key];
  if (value === true) return true;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on';
  }
  return false;
};

export const mergeTimelineParams = (
  params: unknown,
  patch: Record<string, unknown>
): Record<string, unknown> => {
  const next: Record<string, unknown> = isRecord(params) ? { ...params } : {};
  Object.entries(patch).forEach(([key, value]) => {
    if (
      value === null ||
      typeof value === 'undefined' ||
      value === false ||
      (typeof value === 'string' && value.trim().length === 0)
    ) {
      delete next[key];
      return;
    }
    next[key] = value;
  });
  return next;
};

const normalizeRect = (rect: WorkspaceTimelineLayerSource['rect']): WorkspaceTimelineRect => ({
  x: toFiniteTimelineMs(rect?.x) ?? 0,
  y: toFiniteTimelineMs(rect?.y) ?? 0,
  w: toFiniteTimelineMs(rect?.w) ?? 0,
  h: toFiniteTimelineMs(rect?.h) ?? 0,
});

const timelineRectSignature = (rect: unknown): string => {
  if (!rect || typeof rect !== 'object') return '0,0,0,0';
  const source = rect as { x?: unknown; y?: unknown; w?: unknown; h?: unknown };
  return [
    toFiniteTimelineMs(source.x) ?? 0,
    toFiniteTimelineMs(source.y) ?? 0,
    toFiniteTimelineMs(source.w) ?? 0,
    toFiniteTimelineMs(source.h) ?? 0,
  ].join(',');
};

/** Ratio a fade must exceed to count as enabled (below it, the ramp is invisible). */
export const TIMELINE_EFFECT_ENABLED_EPSILON = 0.0005;
/** Ratio applied when a fade is toggled on: one third of the track span. */
export const DEFAULT_TIMELINE_EFFECT_RATIO = 1 / 3;

export const normalizeEffectRatio = (value: unknown): number => {
  const parsed = toFiniteTimelineMs(value);
  return parsed === null ? 0 : clampTimelineNumber(parsed, 0, 1);
};

const normalizeAudioGain = (value: unknown): number => {
  const parsed = toFiniteTimelineMs(value);
  return parsed === null ? 1 : clampTimelineNumber(parsed, 0, AUDIO_GAIN_MAX);
};

export { normalizeTimelineTrackPlaybackRate };

const normalizeLayerOpacity = (value: unknown): number => {
  const parsed = toFiniteTimelineMs(value);
  return parsed === null ? 1 : clampTimelineNumber(parsed, 0, 1);
};

export const getWorkspaceTimelineLayerKind = (
  layer: { type?: string | null; videoSrc?: string | null } | null | undefined
): WorkspaceTimelineLayerKind => {
  if (!layer) return null;
  if (layer.type === 'audio') {
    const source = typeof layer.videoSrc === 'string' ? layer.videoSrc.trim() : '';
    return source.length > 0 ? 'audio' : null;
  }
  if (layer.type === 'video') {
    const source = typeof layer.videoSrc === 'string' ? layer.videoSrc.trim() : '';
    return source.length > 0 ? 'video' : null;
  }
  return 'image';
};

export const buildWorkspaceTimelineSummarySignature = (args: {
  workspaceId: string;
  workspace: WorkspaceTimelineSummaryWorkspaceSource | null | undefined;
  getLayer: (layerId: string) => WorkspaceTimelineSummaryLayerSource | null | undefined;
}): string => {
  const { workspace, getLayer } = args;
  if (!workspace) return '0|0|0|0|1|0';

  const layerIds = Array.isArray(workspace.layerIds)
    ? workspace.layerIds.filter((layerId): layerId is string => typeof layerId === 'string' && layerId.trim().length > 0)
    : [];

  let hasTimelineTracks = false;
  let hasPlayableMediaLayers = false;
  let hasInitializedImageTimeline = false;
  let explicitMaxTrackEndMs = 0;

  for (const layerId of layerIds) {
    const layer = getLayer(layerId);
    const kind = getWorkspaceTimelineLayerKind(layer);
    if (!kind) continue;

    hasTimelineTracks = true;
    if (kind === 'video' || kind === 'audio') {
      hasPlayableMediaLayers = true;
    }

    const startMsRaw = toFiniteTimelineMs(layer?.timelineStartMs);
    const endMsRaw = toFiniteTimelineMs(layer?.timelineEndMs);
    const startMs = Math.max(0, startMsRaw ?? 0);
    if (endMsRaw !== null && endMsRaw > startMs) {
      explicitMaxTrackEndMs = Math.max(explicitMaxTrackEndMs, endMsRaw);
    }

    if (kind === 'image' && !hasInitializedImageTimeline && (startMsRaw !== null || endMsRaw !== null)) {
      hasInitializedImageTimeline = true;
    }
  }

  const imageTimelineManuallyEnabled = workspace.imageTimelineManuallyEnabled === true;
  const preferVersionView = hasPlayableMediaLayers
    ? workspace.preferVersionPanelOverTimeline === true
    : imageTimelineManuallyEnabled
      ? false
      : true;

  return [
    hasTimelineTracks ? '1' : '0',
    hasPlayableMediaLayers ? '1' : '0',
    hasInitializedImageTimeline ? '1' : '0',
    Math.round(explicitMaxTrackEndMs),
    preferVersionView ? '1' : '0',
    imageTimelineManuallyEnabled ? '1' : '0',
  ].join('|');
};

export const buildWorkspaceTimelineSummary = (args: {
  workspaceId: string;
  workspace: WorkspaceTimelineWorkspaceSource;
  layerRegistry: Record<string, WorkspaceTimelineLayerSource | undefined>;
}): WorkspaceTimelineSummary | null => {
  const { workspaceId, workspace, layerRegistry } = args;
  if (!workspace) return null;

  const signature = buildWorkspaceTimelineSummarySignature({
    workspaceId,
    workspace,
    getLayer: (layerId) => layerRegistry[layerId],
  });
  const [hasTracksRaw, hasPlayableRaw, initializedRaw, explicitMaxTrackEndRaw, preferVersionRaw] = signature.split('|');
  const explicitMaxTrackEndMs = Math.max(0, toFiniteTimelineMs(explicitMaxTrackEndRaw) ?? 0);
  const hasTimelineTracks = hasTracksRaw === '1';
  const persistedDurationMs = Math.max(0, toFiniteTimelineMs(workspace.timelineDurationMs) ?? 0);
  const durationMs = hasTimelineTracks
    ? Math.max(1000, persistedDurationMs, explicitMaxTrackEndMs)
    : persistedDurationMs;
  const cursorMs = Math.max(0, Math.round(toFiniteTimelineMs(workspace.timelineCursorMs) ?? 0));
  const fps = typeof workspace.timelineFps === 'number' && Number.isFinite(workspace.timelineFps)
    ? workspace.timelineFps
    : null;

  return {
    workspaceId,
    hasTimelineTracks,
    hasPlayableMediaLayers: hasPlayableRaw === '1',
    hasInitializedImageTimeline: initializedRaw === '1',
    explicitMaxTrackEndMs,
    durationMs,
    cursorMs,
    fps,
    preferVersionView: preferVersionRaw === '1',
  };
};

export const buildWorkspaceTimelineTracks = (args: {
  workspaceId: string;
  layerIds: string[];
  layerRegistry: Record<string, WorkspaceTimelineLayerSource | undefined>;
  options?: {
    roundTiming?: boolean;
    resolveImageSrc?: (
      layer: WorkspaceTimelineLayerSource,
      layerId: string,
      layerType: WorkspaceTimelineTrackView['layerType']
    ) => string | null;
  };
}): WorkspaceTimelineTrackView[] => {
  const { layerIds, layerRegistry, options } = args;
  const shouldRoundTiming = options?.roundTiming !== false;
  const tracks = layerIds
    .map((layerId, index): WorkspaceTimelineTrackView | null => {
      const layer = layerRegistry[layerId];
      if (!layer) return null;

      const layerKind = getWorkspaceTimelineLayerKind(layer);
      if (!layerKind) return null;

      const isVideoLayer = layer.type === 'video';
      const isAudioLayer = layer.type === 'audio';
      const isAudioOnlyVideoLayer = isVideoLayer && readTimelineParamFlag(layer.params, TIMELINE_AUDIO_ONLY_PARAM_KEY);
      const mediaSource = (isVideoLayer || isAudioLayer) && typeof layer.videoSrc === 'string'
        ? layer.videoSrc.trim()
        : null;
      if ((isVideoLayer || isAudioLayer) && (!mediaSource || mediaSource.length === 0)) return null;

      const startMsRaw = toFiniteTimelineMs(layer.timelineStartMs);
      const endMsRaw = toFiniteTimelineMs(layer.timelineEndMs);
      const startMsValue = startMsRaw ?? 0;
      const startMs = Math.max(0, shouldRoundTiming ? Math.round(startMsValue) : startMsValue);
      const endMs = endMsRaw !== null && endMsRaw > startMs
        ? (shouldRoundTiming ? Math.round(endMsRaw) : endMsRaw)
        : null;
      const sourceInMsValue = toFiniteTimelineMs(layer.timelineSourceInMs) ?? 0;
      const sourceInMs = (isVideoLayer || isAudioLayer)
        ? Math.max(0, shouldRoundTiming ? Math.round(sourceInMsValue) : sourceInMsValue)
        : 0;
      const renderOrderRaw = typeof layer.renderOrder === 'number' && Number.isFinite(layer.renderOrder)
        ? layer.renderOrder
        : index;
      const topLayer = Reflect.get(layer, 'top-layer') === true || layer.topLayer === true;
      const renderOrder = topLayer ? renderOrderRaw + 1_000_000 : renderOrderRaw;
      const layerType: WorkspaceTimelineTrackView['layerType'] = isAudioLayer || isAudioOnlyVideoLayer
        ? 'audio'
        : isVideoLayer
          ? 'video'
          : 'image';
      const laneId = readTimelineParamString(layer.params, TIMELINE_LANE_ID_PARAM_KEY) ?? (layer.id ?? layerId);
      const laneOrderRaw = readTimelineParamNumber(layer.params, TIMELINE_LANE_ORDER_PARAM_KEY);
      const audioGain = normalizeAudioGain(readTimelineParamNumber(layer.params, TIMELINE_AUDIO_GAIN_PARAM_KEY));
      const playbackRate = (layerType === 'video' || layerType === 'audio')
        ? normalizeTimelineTrackPlaybackRate(readTimelineParamNumber(layer.params, TIMELINE_PLAYBACK_RATE_PARAM_KEY))
        : 1;
      const transform = layerType !== 'audio'
        ? resolveStudioLayerTransform(layer.params)
        : { rotationDeg: 0, flipX: false, flipY: false };
      const fadeInRatio = layerType !== 'audio'
        ? normalizeEffectRatio(readTimelineParamNumber(layer.params, TIMELINE_FADE_IN_RATIO_PARAM_KEY))
        : 0;
      const fadeOutRatio = layerType !== 'audio'
        ? normalizeEffectRatio(readTimelineParamNumber(layer.params, TIMELINE_FADE_OUT_RATIO_PARAM_KEY))
        : 0;

      return {
        layerId: layer.id ?? layerId,
        layerType,
        name: typeof layer.name === 'string' && layer.name.trim().length > 0 ? layer.name : (layer.id ?? layerId),
        visible: layer.visible !== false,
        renderOrder,
        topLayer,
        startMs,
        endMs,
        sourceInMs,
        playbackRate,
        rect: normalizeRect(layer.rect),
        scale: typeof layer.scale === 'number' && Number.isFinite(layer.scale) ? layer.scale : 1,
        rotationDeg: transform.rotationDeg,
        flipX: transform.flipX,
        flipY: transform.flipY,
        opacity: layerType !== 'audio' ? normalizeLayerOpacity(layer.opacity) : 1,
        imageSrc: layerType === 'image'
          ? (options?.resolveImageSrc?.(layer, layerId, layerType) ?? (
              typeof layer.imageSrc === 'string' && layer.imageSrc.trim().length > 0
                ? layer.imageSrc.trim()
                : null
            ))
          : null,
        videoSrc: layerType === 'video' ? mediaSource : null,
        audioSrc: layerType === 'audio' ? mediaSource : null,
        mutedInTimeline: isVideoLayer && readTimelineParamFlag(layer.params, TIMELINE_MUTE_AUDIO_PARAM_KEY),
        audioGain,
        separatedFromLayerId: readTimelineParamString(layer.params, TIMELINE_SEPARATED_FROM_LAYER_ID_PARAM_KEY),
        fadeInRatio,
        fadeOutRatio,
        laneId,
        laneOrder: laneOrderRaw ?? renderOrder,
      };
    })
    .filter((track): track is WorkspaceTimelineTrackView => Boolean(track))
    .sort((left, right) => {
      if (left.renderOrder !== right.renderOrder) return left.renderOrder - right.renderOrder;
      return left.layerId.localeCompare(right.layerId);
    });

  const associatedVideoLayerIds = new Set(
    tracks
      .filter((track) => track.layerType === 'audio')
      .map((track) => (typeof track.separatedFromLayerId === 'string' ? track.separatedFromLayerId.trim() : ''))
      .filter((layerId) => layerId.length > 0)
  );
  if (associatedVideoLayerIds.size === 0) return tracks;
  return tracks.map((track) => {
    if (track.layerType !== 'video') return track;
    if (!associatedVideoLayerIds.has(track.layerId) || track.mutedInTimeline) return track;
    return { ...track, mutedInTimeline: true };
  });
};

// Rough expected duration (registry average) so the pending track bounds the wait.
const resolvePendingTrackEtaSuffix = (modelId: string): string => {
  const seconds = STUDIO_MODEL_REGISTRY[modelId.trim() as StudioModelId]?.averageGenerationSeconds;
  return typeof seconds === 'number' && seconds > 0 ? ` · ~${Math.round(seconds)}s` : '';
};

// Same geometry as the kernel's pending track, with the timeline panel's richer label.
const resolvePendingGenerationSignatureEntry = (
  task: WorkspacePendingGenerationTaskSource
): WorkspacePendingGenerationTrackSignatureEntry | null => {
  const track = resolvePendingVideoGenerationTrack(task);
  if (!track || !track.taskId) return null;

  const modelName = resolvePendingTrackModelName(track.model);
  const stageLabel = videoLoopStageLabel(track.model);
  const etaSuffix = resolvePendingTrackEtaSuffix(track.model);

  return {
    ...track,
    label: stageLabel ? `${stageLabel} · ${modelName}${etaSuffix}` : `generating ${modelName}${etaSuffix}`,
  };
};

export const buildWorkspacePendingGenerationSignature = (
  tasks: readonly WorkspacePendingGenerationTaskSource[] | null | undefined,
  workspaceId: string
): string => {
  const normalizedWorkspaceId = typeof workspaceId === 'string' ? workspaceId.trim() : '';
  if (!normalizedWorkspaceId || !Array.isArray(tasks) || tasks.length === 0) return '[]';

  const entries = tasks
    .filter((task) => task.workspaceId === normalizedWorkspaceId)
    .map(resolvePendingGenerationSignatureEntry)
    .filter((entry): entry is WorkspacePendingGenerationTrackSignatureEntry => Boolean(entry))
    .sort((left, right) => {
      if (left.startMs !== right.startMs) return right.startMs - left.startMs;
      if (left.endMs !== right.endMs) return right.endMs - left.endMs;
      return right.taskId.localeCompare(left.taskId);
    });

  return JSON.stringify(entries);
};

export const parseWorkspacePendingGenerationSignature = (
  signature: string
): WorkspacePendingGenerationTrackSignatureEntry[] => {
  if (typeof signature !== 'string' || signature.trim().length === 0 || signature === '[]') return [];
  try {
    const parsed = JSON.parse(signature) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry): WorkspacePendingGenerationTrackSignatureEntry | null => {
        if (!isRecord(entry)) return null;
        const taskId = typeof entry.taskId === 'string' ? entry.taskId : '';
        const label = typeof entry.label === 'string' ? entry.label : '';
        const model = typeof entry.model === 'string' ? entry.model : 'video';
        const startMs = toFiniteTimelineMs(entry.startMs);
        const endMs = toFiniteTimelineMs(entry.endMs);
        const durationMs = toFiniteTimelineMs(entry.durationMs);
        if (!taskId || startMs === null || endMs === null || durationMs === null) return null;
        const outputWidth = toFiniteTimelineMs(entry.outputWidth);
        const outputHeight = toFiniteTimelineMs(entry.outputHeight);
        return {
          taskId,
          label,
          model,
          startMs,
          endMs,
          durationMs,
          outputWidth: outputWidth !== null ? Math.round(outputWidth) : null,
          outputHeight: outputHeight !== null ? Math.round(outputHeight) : null,
        };
      })
      .filter((entry): entry is WorkspacePendingGenerationTrackSignatureEntry => Boolean(entry));
  } catch {
    return [];
  }
};

export const buildWorkspaceTimelineLanes = (
  tracks: WorkspaceTimelineTrackView[]
): WorkspaceTimelineLaneView[] => {
  const laneMap = new Map<string, WorkspaceTimelineLaneView>();
  tracks.forEach((track) => {
    const laneId = typeof track.laneId === 'string' && track.laneId.trim().length > 0
      ? track.laneId
      : track.layerId;
    const existing = laneMap.get(laneId);
    if (existing) {
      existing.trackLayerIds.push(track.layerId);
      existing.laneOrder = Math.min(existing.laneOrder, track.laneOrder);
      return;
    }
    laneMap.set(laneId, {
      laneId,
      laneOrder: track.laneOrder,
      trackLayerIds: [track.layerId],
      previewLayerId: track.layerId,
    });
  });

  return Array.from(laneMap.values())
    .map((lane) => {
      const orderedTrackLayerIds = [...lane.trackLayerIds].sort((leftId, rightId) => {
        const left = tracks.find((track) => track.layerId === leftId);
        const right = tracks.find((track) => track.layerId === rightId);
        if (!left || !right) return leftId.localeCompare(rightId);
        if (left.startMs !== right.startMs) return left.startMs - right.startMs;
        if (left.renderOrder !== right.renderOrder) return left.renderOrder - right.renderOrder;
        return left.layerId.localeCompare(right.layerId);
      });
      return {
        ...lane,
        trackLayerIds: orderedTrackLayerIds,
        previewLayerId: orderedTrackLayerIds[0] ?? lane.previewLayerId,
      };
    })
    .sort((left, right) => {
      if (left.laneOrder !== right.laneOrder) return left.laneOrder - right.laneOrder;
      return left.laneId.localeCompare(right.laneId);
    });
};

const DEFAULT_RESOLVED_TRACK_DURATION_MS = 1000;
// Minimum lifetime of any timeline track/layer: one frame at 30fps (1/30s).
// Timeline timing is stored as integer ms, and a single frame-step from t=0
// rounds the cursor to Math.round(1000/30) = 33, so a 33ms span [0,33) is
// correctly inactive at that cursor. This is the single source of truth —
// every timeline min-span floor imports it so the value cannot drift.
export const MIN_TIMELINE_TRACK_SPAN_MS = 33;
const DEFAULT_MIN_TRACK_SPAN_MS = MIN_TIMELINE_TRACK_SPAN_MS;
const DEFAULT_SPLIT_SNAP_TO_SECOND_THRESHOLD_MS = 120;
const DEFAULT_TRACK_EDGE_SNAP_THRESHOLD_MS = 0;

const normalizeTimelineStringId = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const getWorkspaceTimelineLayerIds = (workspace: WorkspaceTimelineWorkspaceSource): string[] => (
  Array.isArray(workspace?.layerIds)
    ? workspace.layerIds
        .map(normalizeTimelineStringId)
        .filter((layerId) => layerId.length > 0)
    : []
);

const computeFadeBoundaries = (track: {
  startMs: number;
  endMs: number;
  activeDurationMs: number;
  fadeInRatio: number;
  fadeOutRatio: number;
}): { fadeInEndMs: number; fadeOutStartMs: number } => {
  const durationMs = Math.max(1, track.activeDurationMs);
  const fadeInDurationMs = durationMs * clampTimelineNumber(track.fadeInRatio, 0, 1);
  const fadeOutDurationMs = durationMs * clampTimelineNumber(track.fadeOutRatio, 0, 1);
  return {
    fadeInEndMs: clampTimelineNumber(track.startMs + fadeInDurationMs, track.startMs, track.endMs),
    fadeOutStartMs: clampTimelineNumber(track.endMs - fadeOutDurationMs, track.startMs, track.endMs),
  };
};

// Resolves persisted timeline tracks without DOM/media probing. For video/audio
// without explicit end times, callers that render mounted media may overlay their
// probed durations locally; Kernel/agent consumers get deterministic DB timings.
export const resolveWorkspaceTimelineResolvedTracks = (
  tracks: readonly WorkspaceTimelineTrackView[],
  options?: {
    durationMs?: number;
    fallbackTrackDurationMs?: number;
    minTrackSpanMs?: number;
    getMediaDurationMs?: (track: WorkspaceTimelineTrackView) => number | null | undefined;
    roundLaneOrder?: boolean;
    roundTiming?: boolean;
  }
): WorkspaceTimelineResolvedTrackView[] => {
  const defaultMinTrackSpanMs = Math.max(1, Math.round(options?.minTrackSpanMs ?? DEFAULT_MIN_TRACK_SPAN_MS));
  const shouldRoundLaneOrder = options?.roundLaneOrder !== false;
  const shouldRoundTiming = options?.roundTiming !== false;
  const fallbackDurationCandidate = options?.fallbackTrackDurationMs ?? options?.durationMs ?? DEFAULT_RESOLVED_TRACK_DURATION_MS;
  const fallbackDurationMs = Math.max(
    defaultMinTrackSpanMs,
    shouldRoundTiming ? Math.round(fallbackDurationCandidate) : fallbackDurationCandidate
  );

  return tracks.map((track) => {
    const rawStartValue = toFiniteTimelineMs(track.startMs) ?? 0;
    const rawStartMs = Math.max(0, shouldRoundTiming ? Math.round(rawStartValue) : rawStartValue);
    const mediaDurationMsRaw = track.layerType === 'image'
      ? null
      : (options?.getMediaDurationMs?.(track) ?? null);
    const mediaDurationMs = typeof mediaDurationMsRaw === 'number' && Number.isFinite(mediaDurationMsRaw) && mediaDurationMsRaw > 0
      ? Math.max(1, mediaDurationMsRaw)
      : null;
    const minSegmentMs = mediaDurationMs !== null
      ? Math.max(1, Math.min(defaultMinTrackSpanMs, mediaDurationMs))
      : defaultMinTrackSpanMs;
    const explicitEndRawMs = toFiniteTimelineMs(track.endMs);
    const explicitEndMs = explicitEndRawMs !== null
      ? (shouldRoundTiming ? Math.round(explicitEndRawMs) : explicitEndRawMs)
      : null;
    const explicitDurationMs = explicitEndMs !== null && explicitEndMs > rawStartMs
      ? explicitEndMs - rawStartMs
      : null;
    const sourceInValue = toFiniteTimelineMs(track.sourceInMs) ?? 0;
    const sourceInMsRaw = track.layerType !== 'image'
      ? Math.max(0, shouldRoundTiming ? Math.round(sourceInValue) : sourceInValue)
      : 0;
    const sourceInMs = mediaDurationMs !== null
      ? clampTimelineNumber(sourceInMsRaw, 0, Math.max(0, mediaDurationMs - minSegmentMs))
      : sourceInMsRaw;
    const playbackRate = (track.layerType === 'video' || track.layerType === 'audio')
      ? normalizeTimelineTrackPlaybackRate(track.playbackRate)
      : 1;
    const resolvedFallbackDurationMs = mediaDurationMs !== null
      ? Math.max(minSegmentMs, (mediaDurationMs - sourceInMs) / playbackRate)
      : fallbackDurationMs;
    const unclampedActiveDurationMs = explicitDurationMs ?? resolvedFallbackDurationMs;
    const activeDurationMs = mediaDurationMs !== null
      ? clampTimelineNumber(unclampedActiveDurationMs, minSegmentMs, Math.max(minSegmentMs, (mediaDurationMs - sourceInMs) / playbackRate))
      : Math.max(minSegmentMs, unclampedActiveDurationMs);
    const startMs = Math.max(0, rawStartMs);
    const endMs = startMs + activeDurationMs;
    const sourceOutMs = sourceInMs + activeDurationMs * playbackRate;
    const trackLimitDurationMs = mediaDurationMs !== null
      ? Math.max(minSegmentMs, (mediaDurationMs - sourceInMs) / playbackRate)
      : activeDurationMs;
    const fadeInRatio = track.layerType === 'audio' ? 0 : normalizeEffectRatio(track.fadeInRatio);
    const fadeOutRatio = track.layerType === 'audio' ? 0 : normalizeEffectRatio(track.fadeOutRatio);
    const fadeBoundaries = computeFadeBoundaries({
      startMs,
      endMs,
      activeDurationMs,
      fadeInRatio,
      fadeOutRatio,
    });

    return {
      ...track,
      startMs,
      endMs,
      sourceInMs,
      sourceOutMs,
      activeDurationMs,
      mediaDurationMs,
      playbackRate,
      trackLimitEndMs: startMs + trackLimitDurationMs,
      minSegmentMs,
      laneId: normalizeTimelineStringId(track.laneId) || track.layerId,
      laneOrder: Number.isFinite(track.laneOrder)
        ? (shouldRoundLaneOrder ? Math.round(track.laneOrder) : track.laneOrder)
        : track.renderOrder,
      fadeInRatio,
      fadeOutRatio,
      fadeInEndMs: fadeBoundaries.fadeInEndMs,
      fadeOutStartMs: fadeBoundaries.fadeOutStartMs,
    };
  });
};

export const resolveWorkspaceTimelineLaneRows = (
  resolvedTracks: readonly WorkspaceTimelineResolvedTrackView[]
): WorkspaceTimelineLaneRowView[] => {
  const laneMap = new Map<string, WorkspaceTimelineLaneRowView>();
  resolvedTracks.forEach((track) => {
    const laneId = normalizeTimelineStringId(track.laneId) || track.layerId;
    const existing = laneMap.get(laneId);
    if (existing) {
      existing.tracks.push(track);
      existing.laneOrder = Math.min(existing.laneOrder, track.laneOrder);
      return;
    }
    laneMap.set(laneId, {
      laneId,
      laneOrder: track.laneOrder,
      tracks: [track],
      previewLayerId: track.layerId,
    });
  });

  return Array.from(laneMap.values())
    .map((row) => {
      const orderedTracks = [...row.tracks].sort((left, right) => {
        if (left.startMs !== right.startMs) return left.startMs - right.startMs;
        if (left.renderOrder !== right.renderOrder) return left.renderOrder - right.renderOrder;
        return left.layerId.localeCompare(right.layerId);
      });
      return {
        ...row,
        tracks: orderedTracks,
        previewLayerId: orderedTracks[0]?.layerId ?? row.previewLayerId,
      };
    })
    .sort((left, right) => {
      if (left.laneOrder !== right.laneOrder) return left.laneOrder - right.laneOrder;
      return left.laneId.localeCompare(right.laneId);
    });
};

const resolveWorkspaceTimelineSelectedTracks = (args: {
  tracks: readonly WorkspaceTimelineResolvedTrackView[];
  selectedLayerIds?: readonly unknown[] | null;
}): WorkspaceTimelineResolvedTrackView[] => {
  const selectedLayerIdSet = new Set(
    (Array.isArray(args.selectedLayerIds) ? args.selectedLayerIds : [])
      .map(normalizeTimelineStringId)
      .filter((layerId) => layerId.length > 0)
  );
  if (selectedLayerIdSet.size === 0) return [];
  return args.tracks.filter((track) => selectedLayerIdSet.has(track.layerId));
};

export const resolveWorkspaceTimelineDurationMetrics = (args: {
  tracks: readonly WorkspaceTimelineDurationTrackLike[];
  persistedDurationMs?: number | null;
  pendingGenerationTracks?: readonly { endMs: number }[] | null;
  getMediaDurationMs?: (track: WorkspaceTimelineDurationTrackLike) => number | null | undefined;
  fallbackTrackDurationMs?: number;
}): WorkspaceTimelineDurationMetrics => {
  const tracks = Array.isArray(args.tracks) ? args.tracks : [];
  const fallbackTrackDurationMs = Math.max(1, toFiniteTimelineMs(args.fallbackTrackDurationMs) ?? DEFAULT_RESOLVED_TRACK_DURATION_MS);
  const maxDeclaredTrackEndMs = tracks.reduce((max, track) => {
    const endMs = typeof track.endMs === 'number' && Number.isFinite(track.endMs)
      ? track.endMs
      : null;
    if (endMs === null) return max;
    return Math.max(max, endMs);
  }, 0);
  const maxInferredTrackEndMs = tracks.reduce((max, track) => {
    const startMs = Math.max(0, toFiniteTimelineMs(track.startMs) ?? 0);
    const sourceInMs = Math.max(0, toFiniteTimelineMs(track.sourceInMs) ?? 0);
    const explicitEndMs =
      typeof track.endMs === 'number' && Number.isFinite(track.endMs) && track.endMs > startMs
        ? track.endMs
        : null;
    const mediaDurationMsRaw = args.getMediaDurationMs?.(track) ?? null;
    const mediaDurationMs = typeof mediaDurationMsRaw === 'number' && Number.isFinite(mediaDurationMsRaw) && mediaDurationMsRaw > 0
      ? mediaDurationMsRaw
      : null;
    if (explicitEndMs !== null) {
      return Math.max(max, explicitEndMs);
    }
    if (mediaDurationMs === null) return max;
    const inferredActiveEndMs = startMs + Math.max(0, mediaDurationMs - sourceInMs);
    return Math.max(max, inferredActiveEndMs);
  }, 0);
  const pendingGenerationMaxEndMs = (args.pendingGenerationTracks ?? []).reduce(
    (max, track) => Math.max(max, toFiniteTimelineMs(track.endMs) ?? 0),
    0
  );
  const maxTrackEndMs = tracks.reduce((max, track) => {
    const startMs = Math.max(0, toFiniteTimelineMs(track.startMs) ?? 0);
    const explicitEndMs =
      typeof track.endMs === 'number' && Number.isFinite(track.endMs) && track.endMs > startMs
        ? track.endMs
        : null;
    if (explicitEndMs !== null) {
      return Math.max(max, explicitEndMs);
    }
    const mediaDurationMsRaw = args.getMediaDurationMs?.(track) ?? null;
    const mediaDurationMs = typeof mediaDurationMsRaw === 'number' && Number.isFinite(mediaDurationMsRaw) && mediaDurationMsRaw > 0
      ? mediaDurationMsRaw
      : null;
    if (mediaDurationMs !== null) {
      const sourceInMs = Math.max(0, toFiniteTimelineMs(track.sourceInMs) ?? 0);
      return Math.max(max, startMs + Math.max(0, mediaDurationMs - sourceInMs));
    }
    return Math.max(max, startMs + fallbackTrackDurationMs);
  }, 0);
  const timelineEndMs = tracks.length > 0 ? maxTrackEndMs : 0;
  const fallbackCandidate = Math.max(
    timelineEndMs,
    args.persistedDurationMs ?? 0,
    maxDeclaredTrackEndMs,
    maxInferredTrackEndMs,
    pendingGenerationMaxEndMs
  );

  return {
    maxDeclaredTrackEndMs,
    maxInferredTrackEndMs,
    durationMs: Math.max(1000, fallbackCandidate || 0),
  };
};

export const resolveWorkspaceTimelineDisplayTracks = <TTrack extends {
  laneOrder: number;
  renderOrder: number;
  layerId: string;
}>(tracks: readonly TTrack[]): TTrack[] => (
  [...tracks].sort((left, right) => {
    if (left.laneOrder !== right.laneOrder) return right.laneOrder - left.laneOrder;
    if (left.renderOrder !== right.renderOrder) return right.renderOrder - left.renderOrder;
    return left.layerId.localeCompare(right.layerId);
  })
);

export const resolveWorkspaceTimelineActiveTrackId = (args: {
  cursorMs: number;
  visibleResolvedTracks: readonly Pick<WorkspaceTimelineResolvedTrackView, 'layerId' | 'startMs' | 'endMs' | 'renderOrder'>[];
}): string | null => {
  const cursorMs = toFiniteTimelineMs(args.cursorMs) ?? 0;
  const overlappingTracks = (Array.isArray(args.visibleResolvedTracks) ? args.visibleResolvedTracks : []).filter((track) => (
    cursorMs >= track.startMs && cursorMs < track.endMs
  ));
  if (overlappingTracks.length === 0) return null;
  const sortedTracks = [...overlappingTracks].sort((left, right) => {
    if (left.renderOrder !== right.renderOrder) {
      return left.renderOrder - right.renderOrder;
    }
    return left.layerId.localeCompare(right.layerId);
  });
  return sortedTracks[sortedTracks.length - 1]?.layerId ?? null;
};

export const resolveWorkspaceTimelineRange = (args: {
  durationMs: number;
  visibleResolvedTracks: readonly WorkspaceTimelineResolvedTrackView[];
  pendingGenerationTracks?: readonly WorkspacePendingGenerationTrackSignatureEntry[] | null;
}): WorkspaceTimelineRangeView => {
  const durationMs = Math.max(1, Math.round(toFiniteTimelineMs(args.durationMs) ?? DEFAULT_RESOLVED_TRACK_DURATION_MS));
  const visibleResolvedTracks = Array.isArray(args.visibleResolvedTracks) ? args.visibleResolvedTracks : [];
  const hasVisibleTrack = visibleResolvedTracks.length > 0;
  const playbackStartMs = hasVisibleTrack
    ? visibleResolvedTracks.reduce((min, track) => Math.min(min, track.startMs), Number.POSITIVE_INFINITY)
    : 0;
  const playbackEndMs = hasVisibleTrack
    ? visibleResolvedTracks.reduce((max, track) => Math.max(max, track.endMs), 0)
    : durationMs;
  const rulerRangeStartMs = hasVisibleTrack ? playbackStartMs : 0;
  const rulerRangeEndMs = hasVisibleTrack
    ? Math.max(playbackStartMs + 1, playbackEndMs)
    : Math.max(1, durationMs);
  let timelineRenderRangeStartMs = rulerRangeStartMs;
  let timelineRenderRangeEndMs = rulerRangeEndMs;

  (args.pendingGenerationTracks ?? []).forEach((track) => {
    timelineRenderRangeStartMs = Math.min(timelineRenderRangeStartMs, track.startMs);
    timelineRenderRangeEndMs = Math.max(timelineRenderRangeEndMs, track.endMs);
  });
  timelineRenderRangeEndMs = Math.max(timelineRenderRangeEndMs, timelineRenderRangeStartMs + 1);

  return {
    playbackStartMs: Number.isFinite(playbackStartMs) ? playbackStartMs : 0,
    playbackEndMs: Math.max(0, playbackEndMs),
    rulerRangeStartMs,
    rulerRangeEndMs,
    rulerRangeMs: Math.max(1, rulerRangeEndMs - rulerRangeStartMs),
    timelineRenderRangeStartMs,
    timelineRenderRangeEndMs,
    timelineRenderRangeMs: Math.max(1, timelineRenderRangeEndMs - timelineRenderRangeStartMs),
  };
};

export const buildWorkspaceTimelineProjection = ({
  projectId,
  workspaceId,
  workspace,
  layerRegistry,
  projectRuntimeSelection,
  pendingTasks,
  options,
}: WorkspaceTimelineProjectionArgs): WorkspaceTimelineProjection | null => {
  const normalizedWorkspaceId = normalizeTimelineStringId(workspaceId);
  const normalizedProjectId = normalizeTimelineStringId(projectId);
  if (!normalizedWorkspaceId || !normalizedProjectId || !workspace) return null;

  const workspaceProjectId = normalizeTimelineStringId(workspace.projectId);
  if (workspaceProjectId && workspaceProjectId !== normalizedProjectId) return null;

  const summary = buildWorkspaceTimelineSummary({
    workspaceId: normalizedWorkspaceId,
    workspace,
    layerRegistry,
  });
  if (!summary) return null;

  const layerIds = getWorkspaceTimelineLayerIds(workspace);
  const tracks = buildWorkspaceTimelineTracks({
    workspaceId: normalizedWorkspaceId,
    layerIds,
    layerRegistry,
  });
  const resolvedTracks = resolveWorkspaceTimelineResolvedTracks(tracks, {
    durationMs: summary.durationMs,
    fallbackTrackDurationMs: options?.fallbackTrackDurationMs,
    minTrackSpanMs: options?.minTrackSpanMs,
  });
  const visibleResolvedTracks = resolvedTracks.filter((track) => track.visible !== false);
  const lanes = buildWorkspaceTimelineLanes(tracks);
  const laneRows = resolveWorkspaceTimelineLaneRows(resolvedTracks);
  const selectedTracks = resolveWorkspaceTimelineSelectedTracks({
    tracks: resolvedTracks,
    selectedLayerIds: projectRuntimeSelection,
  });
  const pendingGenerationTracks = (pendingTasks ?? [])
    .filter((task) => task.workspaceId === normalizedWorkspaceId)
    .map(resolvePendingGenerationSignatureEntry)
    .filter((entry): entry is WorkspacePendingGenerationTrackSignatureEntry => Boolean(entry));

  return {
    workspaceId: normalizedWorkspaceId,
    projectId: normalizedProjectId,
    summary,
    trackLayerIds: tracks.map((track) => track.layerId),
    tracks,
    resolvedTracks,
    visibleResolvedTracks,
    lanes,
    laneRows,
    selectedTrackLayerIds: selectedTracks.map((track) => track.layerId),
    selectedTracks,
    pendingGenerationTracks,
    range: resolveWorkspaceTimelineRange({
      durationMs: summary.durationMs,
      visibleResolvedTracks,
      pendingGenerationTracks,
    }),
  };
};

/** Raw persisted timing field, as stored on a layer DTO. */
type TimelineTrackTimingValue = string | number | null | undefined;

/** Patch applied on top of a layer DTO; absent keys stay untouched. */
export interface TimelineTrackTimingPatch {
  timelineSourceInMs?: number;
  timelineEndMs?: number;
}

export interface TimelineTrackTrimFrontArgs {
  startMs: TimelineTrackTimingValue;
  endMs: TimelineTrackTimingValue;
  sourceInMs: TimelineTrackTimingValue;
  /** Already-normalized playback rate of the track. */
  playbackRate: number;
  /** Probed media duration; null when the media length is still unknown. */
  mediaDurationMs: number | null;
  nextSourceInMs: number;
}

export interface TimelineTrackTrimBackArgs {
  startMs: TimelineTrackTimingValue;
  sourceInMs: TimelineTrackTimingValue;
  playbackRate: number;
  mediaDurationMs: number | null;
  nextBackTrimMs: number;
}

export interface TimelineTrackPlaybackRateArgs {
  startMs: TimelineTrackTimingValue;
  endMs: TimelineTrackTimingValue;
  sourceInMs: TimelineTrackTimingValue;
  /** Already-normalized rate currently persisted on the track. */
  currentPlaybackRate: number;
  /** Already-normalized rate the user is moving to. */
  nextPlaybackRate: number;
  mediaDurationMs: number | null;
}

const normalizeTimelineTrackStartMs = (value: TimelineTrackTimingValue): number =>
  Math.max(0, toFiniteTimelineMs(value) ?? 0);

// Timeline span currently occupied by the track: its explicit end when there is
// one, otherwise the remaining media at the current rate, floored at the min span.
const resolveCurrentTimelineDurationMs = (args: {
  startMs: number;
  endMs: TimelineTrackTimingValue;
  sourceInMs: number;
  playbackRate: number;
  mediaDurationMs: number | null;
}): number => {
  const endMsRaw = toFiniteTimelineMs(args.endMs);
  if (endMsRaw !== null && endMsRaw > args.startMs) return endMsRaw - args.startMs;
  return Math.max(
    MIN_TIMELINE_TRACK_SPAN_MS,
    args.mediaDurationMs !== null
      ? Math.max(0, args.mediaDurationMs - args.sourceInMs) / args.playbackRate
      : DEFAULT_RESOLVED_TRACK_DURATION_MS
  );
};

/**
 * Front trim: moves the media in-point while keeping the back trim untouched.
 * Without a known media duration only the in-point can be written.
 */
export const resolveTimelineTrackTrimFrontPatch = (
  args: TimelineTrackTrimFrontArgs
): TimelineTrackTimingPatch => {
  if (args.mediaDurationMs === null) return { timelineSourceInMs: args.nextSourceInMs };

  const startMs = normalizeTimelineTrackStartMs(args.startMs);
  const sourceInMs = normalizeTimelineTrackStartMs(args.sourceInMs);
  const currentTimelineDurationMs = resolveCurrentTimelineDurationMs({
    startMs,
    endMs: args.endMs,
    sourceInMs,
    playbackRate: args.playbackRate,
    mediaDurationMs: args.mediaDurationMs,
  });
  const minSourceSpanMs = MIN_TIMELINE_TRACK_SPAN_MS * args.playbackRate;
  const currentSourceSpanMs = currentTimelineDurationMs * args.playbackRate;
  const currentBackTrimMs = Math.max(0, args.mediaDurationMs - sourceInMs - currentSourceSpanMs);
  const clampedSourceInMs = clampTimelineNumber(
    args.nextSourceInMs,
    0,
    Math.max(0, args.mediaDurationMs - minSourceSpanMs)
  );
  const nextSourceSpanMs = Math.max(
    minSourceSpanMs,
    args.mediaDurationMs - clampedSourceInMs - currentBackTrimMs
  );

  return {
    timelineSourceInMs: Math.round(clampedSourceInMs),
    timelineEndMs: Math.round(startMs + (nextSourceSpanMs / args.playbackRate)),
  };
};

/**
 * Back trim: shortens the tail while keeping the in-point. Nothing can be
 * resolved without a media duration, so the patch is then empty.
 */
export const resolveTimelineTrackTrimBackPatch = (
  args: TimelineTrackTrimBackArgs
): TimelineTrackTimingPatch => {
  if (args.mediaDurationMs === null) return {};

  const startMs = normalizeTimelineTrackStartMs(args.startMs);
  const sourceInMs = normalizeTimelineTrackStartMs(args.sourceInMs);
  const minSourceSpanMs = MIN_TIMELINE_TRACK_SPAN_MS * args.playbackRate;
  const clampedBackTrimMs = clampTimelineNumber(
    args.nextBackTrimMs,
    0,
    Math.max(0, args.mediaDurationMs - sourceInMs - minSourceSpanMs)
  );
  const nextSourceSpanMs = Math.max(
    minSourceSpanMs,
    args.mediaDurationMs - sourceInMs - clampedBackTrimMs
  );

  return { timelineEndMs: Math.round(startMs + (nextSourceSpanMs / args.playbackRate)) };
};

/**
 * Speed change: preserves the consumed media span, so the timeline span shrinks
 * or grows by the rate ratio instead of playing a different part of the media.
 */
export const resolveTimelineTrackPlaybackRatePatch = (
  args: TimelineTrackPlaybackRateArgs
): TimelineTrackTimingPatch => {
  const startMs = normalizeTimelineTrackStartMs(args.startMs);
  const sourceInMs = normalizeTimelineTrackStartMs(args.sourceInMs);
  const currentTimelineDurationMs = resolveCurrentTimelineDurationMs({
    startMs,
    endMs: args.endMs,
    sourceInMs,
    playbackRate: args.currentPlaybackRate,
    mediaDurationMs: args.mediaDurationMs,
  });
  const minSourceSpanMs = MIN_TIMELINE_TRACK_SPAN_MS * args.nextPlaybackRate;
  const maxSourceSpanMs = args.mediaDurationMs !== null
    ? Math.max(minSourceSpanMs, args.mediaDurationMs - sourceInMs)
    : Number.MAX_SAFE_INTEGER;
  const preservedSourceSpanMs = clampTimelineNumber(
    currentTimelineDurationMs * args.currentPlaybackRate,
    minSourceSpanMs,
    maxSourceSpanMs
  );

  return { timelineEndMs: Math.round(startMs + (preservedSourceSpanMs / args.nextPlaybackRate)) };
};

const getTrackPointerEndMs = (track: WorkspaceTimelinePointerTrackLike): number => {
  const startMs = Math.max(0, toFiniteTimelineMs(track.startMs) ?? 0);
  const endMs = toFiniteTimelineMs(track.endMs);
  return endMs !== null && endMs > startMs ? endMs : startMs;
};

const resolveTimelineMsAtClientX = (args: {
  laneElement: WorkspaceTimelineLaneElementLike | null;
  clientX: number;
  timelineRenderRangeStartMs: number;
  timelineRenderRangeMs: number;
}): number | null => {
  const laneRect = args.laneElement?.getBoundingClientRect() ?? null;
  if (!laneRect || !Number.isFinite(laneRect.width) || laneRect.width <= 0) return null;
  const rangeStartMs = Math.round(toFiniteTimelineMs(args.timelineRenderRangeStartMs) ?? 0);
  const rangeMs = Math.max(1, toFiniteTimelineMs(args.timelineRenderRangeMs) ?? 1);
  const relativeX = clampTimelineNumber(args.clientX - laneRect.left, 0, laneRect.width);
  const relativeMs = (relativeX / laneRect.width) * rangeMs;
  return rangeStartMs + relativeMs;
};

// UI-specific pointer resolver: accepts clientX/layout data and remains separate
// from DOM-free timeline projection/select/lookup helpers used by agentic flows.
export const resolveWorkspaceTimelineTrackAtClientX = <TTrack extends WorkspaceTimelinePointerTrackLike>(
  args: WorkspaceTimelinePointerResolveArgs<TTrack>
): TTrack | null => {
  const laneTracks = Array.isArray(args.laneTracks) ? args.laneTracks : [];
  if (laneTracks.length === 0) return null;
  const timelineMs = resolveTimelineMsAtClientX(args);
  if (timelineMs === null) return laneTracks[0] ?? null;

  const overlappingTracks = laneTracks.filter((track) => (
    timelineMs >= track.startMs && timelineMs <= getTrackPointerEndMs(track)
  ));
  if (overlappingTracks.length > 0) {
    const ordered = [...overlappingTracks].sort((left, right) => {
      if (left.renderOrder !== right.renderOrder) return right.renderOrder - left.renderOrder;
      return left.layerId.localeCompare(right.layerId);
    });
    return ordered[0] ?? null;
  }

  return laneTracks.reduce<TTrack | null>((closest, track) => {
    const distance = Math.min(
      Math.abs(timelineMs - track.startMs),
      Math.abs(timelineMs - getTrackPointerEndMs(track))
    );
    if (!closest) return track;
    const closestDistance = Math.min(
      Math.abs(timelineMs - closest.startMs),
      Math.abs(timelineMs - getTrackPointerEndMs(closest))
    );
    return distance < closestDistance ? track : closest;
  }, null);
};

// UI-specific pointer resolver: accepts clientX/layout data and remains separate
// from DOM-free timeline projection/select/lookup helpers used by agentic flows.
export const resolveWorkspaceTimelineSplitMsAtClientX = <TTrack extends WorkspaceTimelineSplitTrackLike>(
  args: WorkspaceTimelineSplitResolveArgs<TTrack>
): number | null => {
  const timelineMs = resolveTimelineMsAtClientX(args);
  if (timelineMs === null) return null;
  const startMs = Math.max(0, toFiniteTimelineMs(args.track.startMs) ?? 0);
  const endMsRaw = toFiniteTimelineMs(args.track.endMs);
  if (endMsRaw === null || endMsRaw <= startMs) return null;
  const endMs = endMsRaw;
  const snapToSecondThresholdMs = Math.max(
    0,
    toFiniteTimelineMs(args.snapToSecondThresholdMs) ?? DEFAULT_SPLIT_SNAP_TO_SECOND_THRESHOLD_MS
  );
  const edgeSnapThresholdMs = Math.max(
    0,
    toFiniteTimelineMs(args.edgeSnapThresholdMs) ?? DEFAULT_TRACK_EDGE_SNAP_THRESHOLD_MS
  );
  const nearestSecondMs = Math.round(timelineMs / 1000) * 1000;
  let snappedTimelineMs = Math.abs(nearestSecondMs - timelineMs) <= snapToSecondThresholdMs
    ? nearestSecondMs
    : timelineMs;
  if (Math.abs(snappedTimelineMs - startMs) <= edgeSnapThresholdMs) {
    snappedTimelineMs = startMs;
  } else if (Math.abs(snappedTimelineMs - endMs) <= edgeSnapThresholdMs) {
    snappedTimelineMs = endMs;
  }
  return Math.round(clampTimelineNumber(snappedTimelineMs, startMs, endMs));
};
